import { getObjectStatus, OBJECT_STATUS } from 'BrdData/Enum';
import UserData from 'BrdData/UserData';
import { HSCNoWrapText, StatusLabel } from 'Components';
import {
  buildSequelizeRestParams,
  DataTable,
  DataTableColumnHeader,
  DataTableHeaderSwitchFilter,
  DataTableHeaderTextFieldFilter,
} from 'Components/DataTable';
import { QuickViewButton } from 'Components/Buttons';
import { createColumnHelper } from '@tanstack/react-table';
import { SITE_PAGES } from 'Config';
import { getUserNameFromId, useDrawerContext } from 'Context';
import { DataManager } from 'Lib/data';
import UserNameWithDrawerButton from 'Pages/Admin/Users/UserNameWithDrawerButton';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

let DM = new DataManager();

const columnHelper = createColumnHelper();

const UserListView = ({ addNewUserButton }) => {
  const navigate = useNavigate();
  const { closeDrawer } = useDrawerContext();
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    DM.add(UserData.registerListener('RECORD_CREATED', refreshData), UserData);
    DM.add(UserData.registerListener('RECORD_UPDATED', refreshData), UserData);
    DM.add(
      UserData.registerListener('RECORD_ACTIVATED', refreshAndCloseDrawer),
      UserData,
    );
    DM.add(
      UserData.registerListener('RECORD_DEACTIVATED', refreshAndCloseDrawer),
      UserData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };
  const refreshAndCloseDrawer = () => {
    refreshData();
    closeDrawer();
  };

  const isPersonInactive = (user) => {
    return user?.status === OBJECT_STATUS.DEACTIVATED.key || false;
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.full_name, {
        id: 'full_name',
        meta: {
          label: 'Name',
          sortable: true,
          filterFieldName: '*full_name',
          getText: (user) => getUserNameFromId(user.id),
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader column={column} table={table} label='Name'>
            <DataTableHeaderTextFieldFilter
              fieldName={'Name'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => (
          <Link to={`${SITE_PAGES.MANAGE_USERS.path}/${row.original.id}`}>
            {row.original.full_name}
          </Link>
        ),
      }),

      columnHelper.accessor((row) => row.username, {
        id: 'username',
        size: 160,
        meta: {
          label: 'User ID (UNID)',
          sortable: true,
          filterFieldName: '*username',
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader
            column={column}
            table={table}
            label='User ID (UNID)'>
            <DataTableHeaderTextFieldFilter
              fieldName={'User ID'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
      }),

      columnHelper.accessor((row) => row.email, {
        id: 'email',
        size: 224,
        meta: { label: 'Email', sortable: true, getText: (user) => user.email },
        header: ({ column, table }) => (
          <DataTableColumnHeader column={column} table={table} label='Email' />
        ),
        cell: ({ getValue }) =>
          getValue() && (
            <a
              href={`mailto:${getValue()}`}
              onMouseDown={(e) => e.preventDefault()}>
              {getValue()}
            </a>
          ),
      }),

      columnHelper.accessor((row) => row.phone_numbers, {
        id: 'office_phone',
        size: 120,
        meta: { label: 'Office Phone' },
        header: ({ column, table }) => (
          <DataTableColumnHeader
            column={column}
            table={table}
            label='Office Phone'
          />
        ),
        cell: ({ getValue }) => getValue()?.office,
      }),

      columnHelper.accessor((row) => row.notes, {
        id: 'notes',
        size: 220,
        meta: {
          label: 'Notes',
          sortable: true,
          filterFieldName: '*notes',
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader column={column} table={table} label='Notes'>
            <DataTableHeaderTextFieldFilter
              fieldName={'Notes'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
        cell: ({ getValue }) => (
          <HSCNoWrapText tooltipText={'Click to see entire entry'}>
            {getValue()}
          </HSCNoWrapText>
        ),
      }),

      columnHelper.accessor((row) => row.status, {
        id: 'status',
        size: 120,
        meta: {
          label: 'Status',
          centeredHeader: true,
          filterFieldName: '*status',
          getText: (user) => getObjectStatus(user.status)?.label || user.status,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader
            column={column}
            table={table}
            label='Status'
            onOpenFilter={() => {
              if (!column.getFilterValue()?.length) {
                column.setFilterValue([OBJECT_STATUS.ACTIVE]);
              }
            }}>
            <DataTableHeaderSwitchFilter
              fieldName={'Is Active'}
              onFilterValueUpdate={(val) =>
                column.setFilterValue(
                  val ? [OBJECT_STATUS.ACTIVE] : [OBJECT_STATUS.DEACTIVATED],
                )
              }
              checked={
                column.getFilterValue()?.length
                  ? column.getFilterValue()[0] === OBJECT_STATUS.ACTIVE
                  : true
              }
              isStatusSwitch
            />
          </DataTableColumnHeader>
        ),
        cell: ({ getValue }) => (
          <StatusLabel
            soft
            statusKey={getValue()}
            icon={
              getValue() === OBJECT_STATUS.DEACTIVATED.key
                ? 'blocked-person'
                : 'user-check'
            }
          />
        ),
      }),

      columnHelper.display({
        id: 'quick_view',
        size: 36,
        minSize: 36,
        maxSize: 36,
        enableResizing: false,
        enableHiding: false,
        meta: { excludeFromExport: true, isIconCol: true },
        header: () => '',
        cell: ({ row }) => (
          <UserNameWithDrawerButton
            editable
            userId={row.original.id}
            label={<QuickViewButton />}
          />
        ),
      }),
    ],
    [],
  );

  const queryFn = (params) =>
    UserData.getRecordList(
      buildSequelizeRestParams({
        columns,
        ...params,
        defaultSort: 'full_name',
      }),
    ).then(({ records, count }) => ({ rows: records, rowCount: count }));

  return (
    <DataTable
      tableId={'USER'}
      columns={columns}
      queryFn={queryFn}
      refetchToken={refreshKey}
      initialSorting={[{ id: 'full_name', desc: false }]}
      title={'Manage Users'}
      actionButtons={addNewUserButton}
      highlightFunc={isPersonInactive}
      adjustRowHeightToFit
      onRowClick={(user) =>
        navigate(`${SITE_PAGES.MANAGE_USERS.path}/${user.id}`)
      }
    />
  );
};

UserListView.propTypes = {
  addNewUserButton: PropTypes.node,
};

export default UserListView;
